// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import {
  CategoryType,
  HARMONIZED_CATEGORIES,
  getCategoryValues,
  isValidCategory,
} from '@/costs/data/cost-categories';
import {
  Autocomplete,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ForwardedRef, forwardRef } from 'react';

interface Props {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * The placeholder text displayed when no value is selected.
   */
  placeholder?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * The currently selected category.
   */
  value: string | null;

  /**
   * A callback fired when the selected category changes.
   */
  onChange: (value: string | null) => void;

  /**
   * The size of the input field.
   */
  size?: 'small' | 'medium';

  /**
   * The type of category to show ('time' or 'cost'). Defaults to 'cost'.
   */
  type?: CategoryType;
}

/**
 * Renders a cost category autocomplete component with grouped options.
 * This function also forwards a ref to the autocomplete component.
 */
function CostCategoryAutocompleteRenderer(
  props: Props,
  ref: ForwardedRef<HTMLDivElement>
) {
  const categoryType = props.type || 'cost';
  const options = getCategoryValues(categoryType);

  const handleChange = (_: unknown, value: string | null) => {
    // Validate that the value is in our allowed list
    if (value && !isValidCategory(value, categoryType)) {
      props.onChange(null);
      return;
    }
    props.onChange(value);
  };

  // Validate the current value - if it's invalid, show empty
  const validatedValue = isValidCategory(props.value, categoryType)
    ? props.value
    : null;

  return (
    <Autocomplete
      ref={ref}
      size={props.size || 'small'}
      value={validatedValue}
      options={options}
      groupBy={(option) => HARMONIZED_CATEGORIES[option].group}
      getOptionLabel={(option) => option}
      onChange={handleChange}
      isOptionEqualToValue={(option, value) => option === value}
      freeSolo={false}
      disableClearable={false}
      filterSelectedOptions={false}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <Box
            key={key}
            component="li"
            {...otherProps}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body2">{option}</Typography>
            <Tooltip
              title={HARMONIZED_CATEGORIES[option].example}
              placement="right"
              arrow
              enterDelay={200}
            >
              <IconButton
                size="small"
                sx={{
                  padding: 0,
                  minWidth: 16,
                  width: 16,
                  height: 16,
                  ml: 1,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleChange(null, option);
                  // Close the autocomplete and deselect by triggering blur
                  setTimeout(() => {
                    if (e.currentTarget) {
                      const autocompleteInput = e.currentTarget
                        .closest('.MuiAutocomplete-root')
                        ?.querySelector('input');
                      if (autocompleteInput) {
                        (autocompleteInput as HTMLInputElement).blur();
                      }
                    }
                  }, 100);
                }}
              >
                <InfoOutlined sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={props.name}
          label={props.label}
          placeholder={props.placeholder}
          error={props.error}
          helperText={props.helperText}
        />
      )}
    />
  );
}

/**
 * A controlled component for selecting a cost category from a grouped list of options.
 */
export const CostCategoryAutocomplete = forwardRef(
  CostCategoryAutocompleteRenderer
);
